import { Bot, ChevronRight, LoaderCircle, Send, Square } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import {
	CodexModelSelect,
	CodexReasoningEffortSelect,
	reasoningEffortForModel,
} from "@/components/codex-model-select"
import { Button } from "@/components/ui/button"
import { Markdown } from "@/components/ui/markdown"
import { usePlatform } from "@/platform/context"
import type {
	CodexChatItem,
	CodexDiagnostics,
	CodexProjectSession,
	OriginSettings,
} from "@/platform/types"

const statusCopy = (session: CodexProjectSession | null) => {
	if (!session || session.status === "starting") return "Starting Codex…"
	if (session.message) return session.message
	if (session.status === "ready")
		return "Ask Codex to inspect or update this project."
	return "Codex is unavailable."
}

const chatItemName = (item: CodexChatItem) => {
	return (
		item.toolName ?? (item.kind === "agent" ? "Agent activity" : "Tool call")
	)
}

const ChatItem = ({ item }: { item: CodexChatItem }) => {
	const isActivity = item.kind === "tool" || item.kind === "agent"
	if (!isActivity) {
		return (
			<div
				className={`max-w-[92%] rounded-lg px-3 py-2 text-sm ${
					item.role === "user"
						? "ml-auto bg-primary text-primary-foreground"
						: item.kind === "error"
							? "border border-destructive/30 bg-destructive/10 text-destructive"
							: "border bg-card"
				}`}
			>
				{item.role === "assistant" ? (
					<Markdown>{item.text}</Markdown>
				) : (
					<p className="whitespace-pre-wrap break-words">{item.text}</p>
				)}
			</div>
		)
	}

	return (
		<details
			className={`group rounded-lg border text-sm ${
				item.kind === "agent" ? "bg-card" : "bg-muted/40"
			}`}
		>
			<summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 font-medium [&::-webkit-details-marker]:hidden">
				<ChevronRight className="size-3 shrink-0 transition-transform group-open:rotate-90" />
				{item.status === "running" ? (
					<LoaderCircle className="size-3 shrink-0 animate-spin" />
				) : item.kind === "agent" ? (
					<Bot className="size-3 shrink-0" />
				) : null}
				<span className="truncate">{chatItemName(item)}</span>
			</summary>
			<div
				className={`border-t px-3 py-2 text-xs ${
					item.status === "failed"
						? "border-destructive/20 text-destructive"
						: "text-muted-foreground"
				}`}
			>
				<p className="whitespace-pre-wrap break-words">{item.text}</p>
			</div>
		</details>
	)
}

export const ProjectChatSidebar = ({ projectID }: { projectID: string }) => {
	const platform = usePlatform()
	const [diagnostics, setDiagnostics] = useState<CodexDiagnostics | null>(null)
	const [session, setSession] = useState<CodexProjectSession | null>(null)
	const [prompt, setPrompt] = useState("")
	const [error, setError] = useState<string | null>(null)
	const [isUpdatingModel, setIsUpdatingModel] = useState(false)
	const scrollRef = useRef<HTMLDivElement>(null)
	const isAtBottomRef = useRef(true)
	const lastItem = session?.items.at(-1)

	useEffect(() => {
		let mounted = true
		void platform.getCodexSession?.(projectID).then((next) => {
			if (mounted) setSession(next)
		})
		void platform.getCodexDiagnostics?.().then((next) => {
			if (mounted) setDiagnostics(next)
		})
		const unsubscribe = platform.onCodexSession?.((next) => {
			if (next.projectID === projectID) setSession(next)
		})
		return () => {
			mounted = false
			unsubscribe?.()
		}
	}, [platform, projectID])

	const updateCodexSettings = async (patch: Partial<OriginSettings>) => {
		if (!platform.updateSettings) return
		setError(null)
		setIsUpdatingModel(true)
		try {
			const settings = await platform.updateSettings(patch)
			setDiagnostics((current) =>
				current
					? {
							...current,
							selectedModel: settings.codexModel,
							selectedReasoningEffort: settings.codexReasoningEffort,
						}
					: current,
			)
		} catch (updateError) {
			setError(
				updateError instanceof Error
					? updateError.message
					: String(updateError),
			)
		} finally {
			setIsUpdatingModel(false)
		}
	}

	useEffect(() => {
		if (!lastItem || !isAtBottomRef.current) return
		scrollRef.current?.scrollTo({
			behavior: "auto",
			top: scrollRef.current.scrollHeight,
		})
	}, [lastItem])

	const send = async () => {
		const text = prompt.trim()
		if (!text || session?.active || session?.status !== "ready") return
		setPrompt("")
		setError(null)
		try {
			await platform.sendCodexPrompt?.(projectID, text)
		} catch (sendError) {
			setError(
				sendError instanceof Error ? sendError.message : String(sendError),
			)
		}
	}

	return (
		<aside className="flex h-full min-w-0 flex-col border-r bg-background/95">
			<div className="border-b px-4 py-3">
				<p className="text-sm font-semibold">Project assistant</p>
				<p className="mt-0.5 text-xs text-muted-foreground">
					Codex · local project access
				</p>
			</div>
			<div
				ref={scrollRef}
				onScroll={(event) => {
					const { clientHeight, scrollHeight, scrollTop } = event.currentTarget
					isAtBottomRef.current = scrollHeight - scrollTop - clientHeight <= 1
				}}
				className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3"
			>
				{session?.items.length ? (
					session.items.map((item) => <ChatItem item={item} key={item.id} />)
				) : (
					<div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
						{statusCopy(session)}
					</div>
				)}
				{error ? <p className="text-xs text-destructive">{error}</p> : null}
			</div>
			<div className="border-t p-3">
				<textarea
					value={prompt}
					onChange={(event) => setPrompt(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter" && !event.shiftKey) {
							event.preventDefault()
							void send()
						}
					}}
					placeholder={
						session?.status === "ready"
							? "Ask about this project…"
							: statusCopy(session)
					}
					disabled={session?.status !== "ready"}
					rows={3}
					className="w-full resize-none rounded-md border bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring disabled:opacity-60"
				/>
				<div className="mt-2 flex items-center justify-between gap-2">
					<CodexModelSelect
						className="h-8 min-w-0 flex-1 text-xs"
						diagnostics={diagnostics}
						disabled={session?.active || isUpdatingModel}
						onValueChange={(codexModel) =>
							void updateCodexSettings({
								codexModel,
								codexReasoningEffort: reasoningEffortForModel(
									diagnostics,
									codexModel,
									diagnostics?.selectedReasoningEffort ?? "medium",
								),
							})
						}
						value={diagnostics?.selectedModel ?? null}
					/>
					<CodexReasoningEffortSelect
						className="h-8 w-24 text-xs"
						diagnostics={diagnostics}
						disabled={session?.active || isUpdatingModel}
						model={diagnostics?.selectedModel ?? null}
						onValueChange={(codexReasoningEffort) =>
							void updateCodexSettings({ codexReasoningEffort })
						}
						value={diagnostics?.selectedReasoningEffort ?? "medium"}
					/>
					{session?.active ? (
						<Button
							size="sm"
							variant="outline"
							onClick={() => void platform.interruptCodexTurn?.(projectID)}
						>
							<Square className="size-3" /> Stop
						</Button>
					) : (
						<Button
							size="sm"
							onClick={() => void send()}
							disabled={!prompt.trim() || session?.status !== "ready"}
						>
							<Send className="size-3" /> Send
						</Button>
					)}
				</div>
			</div>
		</aside>
	)
}
